/**
 * DocumentChecklist - Document Requirements Inline Display
 * =========================================================
 * 
 * Displays the document requirements checklist at the top of the case detail view.
 * Shows what documents are required, uploaded, and missing based on case type.
 * 
 * This is a HARD GATE - cases cannot proceed to Accountant queue without all required docs.
 * 
 * Props:
 *   caseId: string - The draft/report ID
 *   caseType?: string - Override case type (optional, fetched if not provided)
 *   entityType?: string - Override entity type (optional, fetched if not provided)
 *   onCaseTypeChange?: (caseType: string) => void - Callback when case type changes
 *   compact?: boolean - Render in compact mode for sidebar
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../utils/api';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  ChevronDown,
  ChevronUp,
  Loader2,
  Settings2,
  Ban,
  RefreshCw
} from 'lucide-react';

// Types
interface CoverageInfo {
  data_source: 'canonical_data' | 'financial_data' | 'filenames' | 'none';
  months_uploaded: number;
  months_required: number | null;
  years_covered: number[];
  coverage_range: string | null;
  missing_count: number;
  missing_months: Array<{year: number; month: number; display: string}>;
  missing_summary: string | null;
  is_complete: boolean | null;
  discrepancies?: Array<{filename: string; expected: string; issue: string}>;
}

interface DocumentRequirement {
  doc_type: string;
  display_name: string;
  description: string;
  category: string;
  status: 'required' | 'optional' | 'n/a';
  conditional: boolean;
  condition: string | null;
  uploaded: boolean;
  upload_status: string;
  file_ids: string[];
  file_names: string[];
  file_count: number;
  coverage?: CoverageInfo | null;
}

interface CompletionStats {
  required_count: number;
  uploaded_count: number;
  percentage: number;
  ready_for_accountant: boolean;
}

interface Blocker {
  type: string;
  doc_type?: string;
  display_name?: string;
  message: string;
}

interface DocumentRequirementsResponse {
  case_id: string;
  case_type: string;
  case_type_name: string;
  entity_type: string;
  claim_source: string;
  has_pl: boolean;
  requirements: DocumentRequirement[];
  completion: CompletionStats;
  blockers: Blocker[];
  warnings: Blocker[];
  assessed_at: string;
}

interface CaseTypeOption {
  code: string;
  name: string;
}

interface DocumentChecklistProps {
  caseId: string;
  caseType?: string;
  entityType?: string;
  onCaseTypeChange?: (caseType: string) => void;
  onReadinessChange?: (ready: boolean) => void;
  compact?: boolean;
  className?: string;
}

// Case type display helper
const CASE_TYPE_ICONS: Record<string, string> = {
  'BI': '🏢',
  'FC': '🔒',
  'LP': '📉',
  'FRAUD': '🔍',
  'VAL': '💰',
  'PD': '🤝',
  'CONTRACT': '📜',
  'TRADE': '🔐',
  'EMP_WT': '👨‍💼',
  'EMP_WH': '⏰',
  'EMP_DISC': '⚖️',
  'FAM_DIV': '👪',
  'FAM_CS': '👶',
  'PI_WAGE': '🏥',
  'PI_EARN': '📊',
  'CUSTOM': '⚙️',
};

export default function DocumentChecklist({
  caseId,
  caseType: propCaseType,
  entityType: propEntityType,
  onCaseTypeChange,
  onReadinessChange,
  compact = false,
  className = ''
}: DocumentChecklistProps) {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requirements, setRequirements] = useState<DocumentRequirementsResponse | null>(null);
  const [caseTypes, setCaseTypes] = useState<CaseTypeOption[]>([]);
  const [expanded, setExpanded] = useState(!compact);
  const [showCaseTypeSelector, setShowCaseTypeSelector] = useState(false);
  const [updatingCaseType, setUpdatingCaseType] = useState(false);

  // Fetch case types once
  useEffect(() => {
    const fetchCaseTypes = async () => {
      try {
        const response = await api.get('/api/v1/case-types', { showErrorToast: false });
        setCaseTypes(response.case_types || []);
      } catch (err) {
        console.warn('Failed to fetch case types:', err);
      }
    };
    fetchCaseTypes();
  }, []);

  // Fetch document requirements
  const fetchRequirements = useCallback(async () => {
    if (!caseId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(
        `/api/v1/reports/${caseId}/document-requirements`,
        { showErrorToast: false }
      );
      setRequirements(response);
      
      // Notify parent of readiness change
      if (onReadinessChange) {
        onReadinessChange(response.completion?.ready_for_accountant || false);
      }
    } catch (err: any) {
      if (err.status === 404) {
        setError('Case not found');
      } else {
        setError('Failed to load document requirements');
      }
    } finally {
      setLoading(false);
    }
  }, [caseId, onReadinessChange]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  // Update case type
  const handleCaseTypeChange = async (newCaseType: string) => {
    setUpdatingCaseType(true);
    try {
      await api.post(`/api/v1/reports/${caseId}/case-type`, {
        case_type: newCaseType
      }, { showErrorToast: false });
      
      // Refresh requirements
      await fetchRequirements();
      
      // Notify parent
      if (onCaseTypeChange) {
        onCaseTypeChange(newCaseType);
      }
      
      setShowCaseTypeSelector(false);
    } catch (err) {
      console.error('Failed to update case type:', err);
    } finally {
      setUpdatingCaseType(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Loading document requirements...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={`bg-red-50 rounded-lg border border-red-200 p-4 ${className}`}>
        <div className="flex items-center gap-2 text-red-600">
          <XCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!requirements) return null;

  const { completion, blockers, warnings } = requirements;
  const requiredDocs = requirements.requirements.filter(r => r.status === 'required');
  const optionalDocs = requirements.requirements.filter(r => r.status === 'optional');
  const naiveDocs = requirements.requirements.filter(r => r.status === 'n/a');

  // Compact mode render
  if (compact) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 ${className}`}>
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <span className="font-medium text-sm">Document Checklist</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              completion.ready_for_accountant 
                ? 'bg-green-100 text-green-700' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              {completion.uploaded_count}/{completion.required_count}
            </span>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {/* Expanded content */}
        {expanded && (
          <div className="p-3 pt-0 border-t border-gray-100">
            {renderDocumentList()}
          </div>
        )}
      </div>
    );
  }

  // Full mode render - inline at top of case detail
  function renderDocumentList() {
    return (
      <div className="space-y-2 mt-2">
        {requiredDocs.map(doc => (
          <div key={doc.doc_type} className="flex items-start gap-2 text-sm">
            {doc.uploaded ? (
              <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className={doc.uploaded ? 'text-gray-700' : 'text-red-700 font-medium'}>
                  {doc.display_name}
                </span>
                {doc.conditional && doc.condition && (
                  <span className="text-xs text-gray-400" title={doc.condition}>*</span>
                )}
              </div>
              {doc.uploaded && doc.file_count > 0 && (
                <span className="text-xs text-gray-500">
                  {doc.file_count} file{doc.file_count > 1 ? 's' : ''} uploaded
                </span>
              )}
            </div>
          </div>
        ))}
        
        {/* Not Applicable items (collapsed) */}
        {naiveDocs.length > 0 && (
          <div className="text-xs text-gray-400 pt-1 border-t border-gray-100 mt-2">
            <span className="flex items-center gap-1">
              <Ban className="w-3 h-3" />
              {naiveDocs.length} not required for this case
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}>
      {/* Header with case type and completion status */}
      <div className="bg-gradient-to-r from-blue-50 to-white p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl" title={requirements.case_type_name}>
              {CASE_TYPE_ICONS[requirements.case_type] || '📋'}
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">
                  Document Checklist
                </h3>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  completion.ready_for_accountant 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {completion.uploaded_count} of {completion.required_count} ✓
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span>Case Type: {requirements.case_type_name}</span>
                {requirements.entity_type !== 'unknown' && (
                  <>
                    <span>•</span>
                    <span className="capitalize">{requirements.entity_type.replace('_', ' ')}</span>
                  </>
                )}
                {requirements.claim_source && requirements.claim_source !== 'unknown' && (
                  <>
                    <span>•</span>
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      requirements.claim_source === 'first_party' 
                        ? 'bg-blue-100 text-blue-700' 
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {requirements.claim_source === 'first_party' ? '1st Party' : '3rd Party'}
                    </span>
                  </>
                )}
                <button
                  onClick={() => setShowCaseTypeSelector(!showCaseTypeSelector)}
                  className="ml-1 p-1 hover:bg-gray-200 rounded transition-colors"
                  title="Change case type"
                >
                  <Settings2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
          
          <button
            onClick={fetchRequirements}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        
        {/* Case Type Selector (hidden by default) */}
        {showCaseTypeSelector && (
          <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
            <label className="block text-xs font-medium text-gray-600 mb-2">Change Case Type</label>
            <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
              {caseTypes.map(ct => (
                <button
                  key={ct.code}
                  onClick={() => handleCaseTypeChange(ct.code)}
                  disabled={updatingCaseType}
                  className={`text-left px-2 py-1.5 text-xs rounded border transition-colors ${
                    requirements.case_type === ct.code
                      ? 'bg-blue-50 border-blue-300 text-blue-700'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <span className="mr-1">{CASE_TYPE_ICONS[ct.code] || '📋'}</span>
                  {ct.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Progress bar */}
      <div className="px-4 py-2 bg-gray-50">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className={`h-full transition-all duration-300 ${
              completion.ready_for_accountant ? 'bg-green-500' : 'bg-blue-500'
            }`}
            style={{ width: `${completion.percentage}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{completion.percentage}% complete</span>
          <span>
            {completion.ready_for_accountant 
              ? '✓ Ready for Accountant' 
              : `${completion.required_count - completion.uploaded_count} required documents missing`
            }
          </span>
        </div>
      </div>
      
      {/* Document List */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Required Documents */}
          {requiredDocs.map(doc => (
            <div 
              key={doc.doc_type}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                doc.uploaded 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-red-50 border-red-200'
              }`}
            >
              {doc.uploaded ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900">
                  {doc.display_name}
                </div>
                {doc.uploaded ? (
                  <div className="text-xs text-green-700 mt-0.5">
                    {doc.file_count} file{doc.file_count > 1 ? 's' : ''} uploaded
                    {/* Show coverage info for time-series documents */}
                    {doc.coverage && doc.coverage.coverage_range && (
                      <span className="block text-green-600 font-medium">
                        📅 {doc.coverage.coverage_range} ({doc.coverage.months_uploaded} months)
                        {doc.coverage.data_source === 'canonical_data' && (
                          <span className="ml-1 text-xs text-gray-400" title="Verified from parsed data">✓ verified</span>
                        )}
                      </span>
                    )}
                    {/* Show missing months if any */}
                    {doc.coverage && doc.coverage.missing_count > 0 && (
                      <span className="block text-amber-600 mt-1">
                        ⚠️ Missing: {doc.coverage.missing_summary}
                      </span>
                    )}
                    {/* Show discrepancies if found */}
                    {doc.coverage && doc.coverage.discrepancies && doc.coverage.discrepancies.length > 0 && (
                      <span className="block text-red-500 mt-1">
                        🚨 Data mismatch: {doc.coverage.discrepancies[0].issue}
                      </span>
                    )}
                    {doc.file_names.length > 0 && !doc.coverage?.coverage_range && (
                      <span className="block text-gray-500 truncate" title={doc.file_names.join(', ')}>
                        {doc.file_names[0]}{doc.file_names.length > 1 ? ` +${doc.file_names.length - 1} more` : ''}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-red-700 mt-0.5">
                    {/* Show what's specifically missing */}
                    {doc.coverage && doc.coverage.months_required ? (
                      <>
                        <span className="font-medium">Missing {doc.coverage.months_required} months of data</span>
                        {doc.coverage.missing_months && doc.coverage.missing_months.length > 0 && (
                          <span className="block text-red-600 mt-1">
                            Need: {doc.coverage.missing_months.slice(0, 3).map(m => m.display).join(', ')}
                            {doc.coverage.missing_months.length > 3 && ` +${doc.coverage.missing_months.length - 3} more`}
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        Missing - required for this case type
                        {doc.conditional && doc.condition && (
                          <span className="block text-gray-500">{doc.condition}</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {/* Optional Documents (collapsed) */}
        {optionalDocs.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <details className="group">
              <summary className="flex items-center gap-2 cursor-pointer text-sm text-gray-600 hover:text-gray-900">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                <span>Optional Documents ({optionalDocs.length})</span>
                <ChevronDown className="w-4 h-4 ml-auto group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                {optionalDocs.map(doc => (
                  <div key={doc.doc_type} className="flex items-center gap-2 text-sm p-2 bg-gray-50 rounded">
                    {doc.uploaded ? (
                      <CheckCircle2 className="w-4 h-4 text-gray-400" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-amber-400" />
                    )}
                    <span className="text-gray-600">{doc.display_name}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
        
        {/* Not Applicable Documents (collapsed) */}
        {naiveDocs.length > 0 && (
          <div className="mt-3">
            <details className="group">
              <summary className="flex items-center gap-2 cursor-pointer text-xs text-gray-400 hover:text-gray-600">
                <Ban className="w-3 h-3" />
                <span>Not applicable for this case ({naiveDocs.length})</span>
                <ChevronDown className="w-3 h-3 ml-auto group-open:rotate-180 transition-transform" />
              </summary>
              <div className="mt-2 flex flex-wrap gap-1">
                {naiveDocs.map(doc => (
                  <span key={doc.doc_type} className="text-xs text-gray-400 px-2 py-0.5 bg-gray-100 rounded">
                    {doc.display_name}
                    {doc.condition && <span className="ml-1" title={doc.condition}>*</span>}
                  </span>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
      
      {/* Blocker Banner - shows when not ready */}
      {!completion.ready_for_accountant && blockers.length > 0 && (
        <div className="bg-red-50 border-t border-red-200 p-4">
          <div className="flex items-start gap-2">
            <Ban className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-red-800">Cannot proceed to Accountant</p>
              <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                {blockers.slice(0, 3).map((b, i) => (
                  <li key={i}>{b.message}</li>
                ))}
                {blockers.length > 3 && (
                  <li>...and {blockers.length - 3} more</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Ready Banner */}
      {completion.ready_for_accountant && (
        <div className="bg-green-50 border-t border-green-200 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <p className="text-green-800 font-medium">
              All required documents collected - ready for Accountant review
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
